import React, { useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import { useTheme } from '@mui/material/styles'
import { useIntersectionObserver } from 'usehooks-ts'
import Carousel from 'nuka-carousel'
import { ButtonBase, Container, Typography, useMediaQuery } from '@mui/material'
import CarouselArrowLeftIcon from '@icons/CarouselArrowLeftIcon'
import CarouselArrowRightIcon from '@icons/CarouselArrowRightIcon'
import { DynamicProductFragment } from '@src/fragments/DynamicProduct.generated'
import DynamicProductCard from './DynamicProductCard'
import { sendCarouselPromoEvent } from '@src/utils/analytics/carouselAnalytics'
import {
  useCategorizePage,
  ProductCardTypeToPageMap,
} from '@utils/analytics/CategorizePage'
import useResponsiveSizes from '@utils/useResponsiveSizes'
import ProductQuickLook from '@src/components/ProductQuickLook/ProductQuickLook'
import {
  CarouselProduct,
  CarouselProductTypes,
  CAROUSEL_TITLE_LOOKUP_BY_TYPE,
  ProductCardProps,
} from './DynamicProductCardTypes'
import {
  handleSlideMovement,
  ProductCarouselControlProps,
} from './slideMovementHandler'
import { makeListIdAndNameFields } from '@src/utils/analytics/analyticsUtils'

//Trigger the observer callback when the element is at least 33% visible.
export const INTERSECTION_OBSERVER_THRESHOLD = 0.33

export const useStyles = makeStyles<void, 'productHeroTile'>()((
  theme,
  _params,
  classes
) => {
  return {
    titleHeader: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    withoutControls: {
      padding: 0,
    },
    content: {
      paddingTop: theme.spacing(8),
      paddingBottom: theme.spacing(20),
      // Adds visible keyboard focus since the div element is tabbable (though not interactive)
      '& .slider-frame': {
        border: '2px solid transparent',
        '&:focus-visible': {
          border: `2px solid ${theme.palette.primary.main}`,
          borderRadius: theme.shape.borderRadius,
        },
      },
      [theme.breakpoints.up('sm')]: {
        paddingBottom: theme.spacing(8),
      },
    },
    contentInTab: {
      paddingTop: theme.spacing(4),
    },
    contentCondensed: {
      padding: 0,
    },
    navBtn: {},
    navBtnSmall: {
      height: 24,
      width: 24,
    },
    navBtnLarge: {
      height: 32,
      width: 32,
      display: 'none',
      [theme.breakpoints.up('sm')]: {
        display: 'flex',
      },
    },
    navBtnLeft: {
      transform: 'translateX(-40px)',
    },
    mobileBottomControls: {
      width: 105,
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      bottom: -44,
      justifyContent: 'space-between',
      [theme.breakpoints.up('sm')]: {
        display: 'none',
      },
    },
    mobileBottomControlsHeroCard: {
      display: 'flex',
    },
    icon: {
      fontSize: theme.typography.pxToRem(14),
      color: theme.palette.primary.main,
      width: '1em',
    },
    iconDisabled: {
      color: theme.palette.grey[500],
    },
    cartRecommended: {
      backgroundColor: theme.palette.background.grey,
    },
    card: {
      [theme.breakpoints.up('sm')]: {
        paddingRight: theme.spacing(10),
      },
    },
    productHeroCardContent: {
      padding: 0,
      [theme.breakpoints.down('sm')]: {
        touchAction: 'pan-y',
      },
      [`& .${classes.productHeroTile} button`]: {
        '@media (min-width: 900px) and (max-width: 1300px)': {
          whiteSpace: 'normal',
        },
      },
    },
    productHeroTile: {
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(0, 4),
      },
    },
  }
})

const useTitleStyles = makeStyles<void, 'contentCondensed'>()((
  theme,
  _params,
  classes
) => {
  return {
    contentCondensed: {
      padding: 0,
    },
    title: {
      marginBottom: theme.spacing(8),
      [`.${classes.contentCondensed} &`]: {
        marginBottom: theme.spacing(4),
      },
    },
    productHeroCardTitle: {
      margin: `${theme.spacing(
        0,
        0,
        theme.typography.pxToRem(40),
        0
      )} !important`,
      fontSize: `${theme.typography.pxToRem(18)} !important`,
      [theme.breakpoints.up('sm')]: {
        margin: `${theme.spacing(
          0,
          0,
          theme.typography.pxToRem(24),
          3
        )} !important`,
      },
    },
  }
})

interface ProductCarouselControlsConfig {
  productHeroCard?: boolean
  products: CarouselProduct[]
  creativeSlot: string
  carouselType: CarouselProductTypes
  pageType?: string | null | undefined
  pageTypeDetails?: string | null | undefined
}

export const CarouselTitle = ({
  title,
  isHero,
  type,
}: {
  title?: string | JSX.Element
  isHero: boolean
  type: CarouselProductTypes
}) => {
  const { classes: titleClasses, cx } = useTitleStyles()

  const renderTitle = (): React.ReactNode => {
    if (title) {
      return title
    }

    if (CAROUSEL_TITLE_LOOKUP_BY_TYPE[type]) {
      return <FormattedMessage {...CAROUSEL_TITLE_LOOKUP_BY_TYPE[type]} />
    }

    // Replaces the dashes and under scores in the type name with spaces for readability.
    // If the type is for some reason not found on the PRODUCT_CARD_TYPE_TRANSLATED_TITLE object.
    // Should never happen since it is type checked at build but APIs have burned us before.
    return type?.replace(/[-_]/g, ' ')
  }

  const titleContent = renderTitle()

  // If title is a JSX element, render it directly without Typography wrapper
  if (title && typeof title !== 'string') {
    return <>{titleContent}</>
  }

  // Otherwise, wrap in Typography
  return (
    <Typography
      variant="h2"
      className={cx(
        titleClasses.title,
        isHero && titleClasses.productHeroCardTitle
      )}
    >
      {titleContent}
    </Typography>
  )
}
export const useProductCarouselControls = ({
  productHeroCard,
  products,
  creativeSlot,
  carouselType,
  pageType,
  pageTypeDetails,
}: ProductCarouselControlsConfig) => {
  const { classes, cx } = useStyles()

  return {
    renderCenterLeftControls: (props: ProductCarouselControlProps) => {
      const disabled = props.currentSlide === 0
      return (
        <>
          {!productHeroCard && (
            <ButtonBase
              className={cx(
                classes.navBtn,
                classes.navBtnLarge,
                classes.navBtnLeft
              )}
              disabled={disabled}
              onClick={() =>
                handleSlideMovement(
                  props,
                  products,
                  'prev',
                  creativeSlot,
                  carouselType,
                  pageType,
                  pageTypeDetails
                )
              }
              aria-label="previous"
            >
              <CarouselArrowLeftIcon
                className={cx(classes.icon, {
                  [classes.iconDisabled]: disabled,
                })}
              />
            </ButtonBase>
          )}
        </>
      )
    },
    renderCenterRightControls: (props: ProductCarouselControlProps) => {
      const disabled =
        props.currentSlide + props.slidesToShow === props.slideCount ||
        props.slideCount < props.slidesToShow
      return (
        <>
          {!productHeroCard && (
            <ButtonBase
              disabled={disabled}
              className={cx(classes.navBtn, classes.navBtnLarge)}
              onClick={() =>
                handleSlideMovement(
                  props,
                  products,
                  'next',
                  creativeSlot,
                  carouselType,
                  pageType,
                  pageTypeDetails
                )
              }
              aria-label="next"
            >
              <CarouselArrowRightIcon
                className={cx(classes.icon, {
                  [classes.iconDisabled]: !!props.slidesToScroll && disabled,
                })}
              />
            </ButtonBase>
          )}
        </>
      )
    },
    renderBottomCenterControls: (props: ProductCarouselControlProps) => {
      const previousDisabled = props.currentSlide === 0
      const nextDisabled =
        props.currentSlide + props.slidesToShow === props.slideCount ||
        props.slideCount < props.slidesToShow
      return (
        <div
          className={cx(
            classes.mobileBottomControls,
            productHeroCard && classes.mobileBottomControlsHeroCard
          )}
        >
          <ButtonBase
            disabled={previousDisabled}
            onClick={() =>
              handleSlideMovement(
                props,
                products,
                'prev',
                creativeSlot,
                carouselType,
                pageType,
                pageTypeDetails
              )
            }
            aria-label="previous"
            className={cx(classes.navBtn, classes.navBtnSmall)}
          >
            <CarouselArrowLeftIcon
              className={cx(classes.icon, {
                [classes.iconDisabled]: previousDisabled,
              })}
            />
          </ButtonBase>
          <Typography variant="body2">
            {Math.floor(props.currentSlide / props.slidesToShow) + 1} of{' '}
            {Math.round(props.slideCount / props.slidesToShow)}
          </Typography>
          <ButtonBase
            disabled={nextDisabled}
            onClick={() =>
              handleSlideMovement(
                props,
                products,
                'next',
                creativeSlot,
                carouselType,
                pageType,
                pageTypeDetails
              )
            }
            aria-label="next"
            className={cx(classes.navBtn, classes.navBtnSmall)}
          >
            <CarouselArrowRightIcon
              className={cx(classes.icon, {
                [classes.iconDisabled]: !!props.slidesToScroll && nextDisabled,
              })}
            />
          </ButtonBase>
        </div>
      )
    },
  }
}

const DynamicProductCarousel: React.FC<ProductCardProps> = ({
  type,
  products,
  withoutControls,
  slidesToShow,
  condensed,
  preloadCardImages,
  title,
  headerRight,
  additionalInfoRenderer,
  cardFooterVariant,
  onFooterAction,
  productHeroCard = false,
  isProductRecommendation = false,
  hideProductImg = false,
  displayDiscount,
  analyticsItemListingId,
  analyticsItemListingName,
  gaPricingOverrides,
}) => {
  const { classes, cx } = useStyles()
  const getSize = useResponsiveSizes()

  const pageCategory = useCategorizePage()
  const creativeSlot = `${type} - ${pageCategory}`

  const carouselControls = useProductCarouselControls({
    productHeroCard,
    products,
    creativeSlot,
    carouselType: type,
    pageType: analyticsItemListingId,
    pageTypeDetails: analyticsItemListingName,
  })

  const isCartRecommended =
    type === CarouselProductTypes.CartRecommendedProducts

  const [quickAddProduct, setQuickAddProduct] =
    useState<DynamicProductFragment | null>(null)

  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')) === true

  const carouselRef = useRef(null)
  const [sawCarousel, setSawCarousel] = useState(false)
  const carouselVisbility = useIntersectionObserver(carouselRef, {
    threshold: INTERSECTION_OBSERVER_THRESHOLD,
  })

  const productsByResponsiveSize = getSize({ xs: 1, sm: 2, md: 3, lg: 4 })

  const numProducts =
    productHeroCard && isDesktop ? 2 : slidesToShow || productsByResponsiveSize

  if (!products || products?.length === 0) return null

  if (carouselVisbility?.isIntersecting && !sawCarousel) {
    const slicedVisible = products.slice(0, numProducts)

    sendCarouselPromoEvent({
      creativeSlot,
      carouselType: type,
      promoItemId: analyticsItemListingId,
      promoItemName: analyticsItemListingName,
      products: slicedVisible,
    })
    setSawCarousel(true)
  }

  const listIdAndListNameFields = makeListIdAndNameFields(
    type,
    analyticsItemListingId,
    analyticsItemListingName
  )

  return (
    <div
      data-testid={type}
      id={type}
      className={cx({
        [classes.cartRecommended]: isCartRecommended,
      })}
      ref={carouselRef}
      data-creative_slot={creativeSlot}
      data-event="ga4-ecomm-promotion"
    >
      <Container
        maxWidth="lg"
        className={cx({
          [classes.withoutControls]: withoutControls,
        })}
      >
        <div
          className={cx(
            classes.content,
            {
              [classes.contentCondensed]: condensed,
            },
            productHeroCard && classes.productHeroCardContent,
            isProductRecommendation && classes.contentInTab
          )}
        >
          <div className={classes.titleHeader}>
            <CarouselTitle title={title} isHero={productHeroCard} type={type} />
            {headerRight}
          </div>
          <Carousel
            slidesToScroll={numProducts}
            slidesToShow={numProducts}
            disableEdgeSwiping
            withoutControls={withoutControls}
            {...carouselControls}
          >
            {products.map((product, i) => (
              <DynamicProductCard
                key={i}
                index={i}
                product={product}
                testId={type}
                preloadCardImages={preloadCardImages}
                className={cx(
                  classes.card,
                  productHeroCard && classes.productHeroTile
                )}
                type={type}
                hideProductImg={hideProductImg}
                displayDiscount={displayDiscount}
                additionalInfoRenderer={additionalInfoRenderer}
                cardFooterVariant={cardFooterVariant}
                onFooterAction={
                  onFooterAction || ((product) => setQuickAddProduct(product))
                }
                analyticsItemListingId={analyticsItemListingId}
                analyticsItemListingName={analyticsItemListingName}
                dataAttributes={{
                  'data-item_brand': product.brand.key || product.brand.erpKey,
                  'data-item_id': product.productNumber,
                  'data-item_list_id': listIdAndListNameFields.list_id,
                  'data-item-list_name': listIdAndListNameFields.list_name,
                  'data-gaproductcode': product.gaProductCode || '',
                }}
              />
            ))}
          </Carousel>
        </div>
      </Container>
      {quickAddProduct && (
        <ProductQuickLook
          {...quickAddProduct}
          showAddToList
          open
          image={quickAddProduct.images[0]}
          onClose={() => setQuickAddProduct(null)}
          gaType={ProductCardTypeToPageMap[type]}
          erpType={quickAddProduct?.erp_type || []}
          gaPricingOverrides={gaPricingOverrides}
        />
      )}
    </div>
  )
}

export default DynamicProductCarousel
